import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Download, Lock, Loader2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { apiPhotoStudio, apiPhotoStudioPreview } from "@/lib/api";

const BACKDROPS = [
  { id: "warm-oak", label: "Warm oak", swatch: "#A9825A" },
  { id: "concrete", label: "Concrete", swatch: "#C6C4BF" },
  { id: "grass", label: "Grass", swatch: "#4C8C2B" },
  { id: "studio-white", label: "Studio White", swatch: "#F2F2F0" },
];

function downloadDataUrl(base64, filename) {
  const a = document.createElement("a");
  a.href = `data:image/jpeg;base64,${base64}`;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Reseller / Pro only. Runs the uploaded photo through Nano Banana
 * (gemini-3.1-flash-image-preview) to generate a professional flat-lay
 * listing shot. On-demand — only fires when the user clicks Generate.
 * Each enhancement uses Emergent Universal Key credits (~$0.04 / call).
 */
export default function PhotoStudio({ scanId, plan, category, onUpgrade , onCharged , demo }) {
  // Hard gate: keep in sync with backend `_STUDIO_ALLOWED_CATEGORIES`.
  // Never render the card for collectibles or non-allowlisted categories —
  // Nano Banana consistently mangles readable text (grades, editions, serials),
  // and a warning banner isn't strong enough to stop misuse.

  // Reseller/Pro: included free. Thrifter: 1 credit per item, charged
  // server-side on first generation; all backdrops included after that.
  // Studio is open to every tier — free/thrifter pay 1 credit (charged server-side
  // on first generation), reseller/pro included. The old blurred upsell is retired.
  const unlocked = true;
  const paysCredit = !demo && (plan === "thrifter" || plan === "free" || !plan);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [busy, setBusy] = useState(false);
  // Per-scan cache of already-generated backdrops. In v2 this is still useful
  // as a UX speed-up (segment+composite is ~700ms on CPU), but no longer a
  // credit-saver — v2 costs zero credits either way.
  const [variants, setVariants] = useState({});
  const [backdrop, setBackdrop] = useState("warm-oak");
  const [progress, setProgress] = useState(null);  // {progress:0-100, stage:string} while running
  const [lastError, setLastError] = useState(null); // string message for retry UI
  const result = variants[backdrop] || null;

  // Locked-user preview state (§3): fires ONCE per scan, only when the
  // locked section enters the viewport. Cached server-side per scan_id.
  const cardRef = useRef(null);
  const [preview, setPreview] = useState(null);      // {studio_b64, cached}
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewErr, setPreviewErr] = useState(false);
  const previewTriggeredRef = useRef(false);

  useEffect(() => {
    if (unlocked || !scanId || previewTriggeredRef.current) return;
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !previewTriggeredRef.current) {
          previewTriggeredRef.current = true;
          setPreviewBusy(true);
          apiPhotoStudioPreview(scanId)
            .then((r) => setPreview(r))
            .catch(() => setPreviewErr(true))
            .finally(() => setPreviewBusy(false));
          io.disconnect();
        }
      }
    }, { rootMargin: "200px 0px", threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  }, [scanId, unlocked]);

  // Category gate — placed AFTER all hooks (React rules-of-hooks).
  const ALLOWED = new Set(["apparel", "electronics", "home"]);
  if (category && !ALLOWED.has(String(category).toLowerCase())) return null;

  const runEnhance = async (bd = backdrop) => {
    if (!unlocked) return;
    // Client-side cache hit → skip the API call entirely. Server also
    // caches this on the scan doc for the fast path.
    if (variants[bd]) {
      setBackdrop(bd);
      return;
    }
    setBusy(true);
    setLastError(null);
    setProgress({ progress: 0, stage: "queued" });
    try {
      const r = await apiPhotoStudio(scanId, 0, bd, {
        onProgress: (p) => setProgress({ progress: p.progress ?? 0, stage: p.stage || p.status }),
      });
      setVariants((prev) => ({ ...prev, [bd]: r }));
      if (paysCredit && !hasGenerated) {
        onCharged?.();
        toast.success("1 credit spent — all backdrops unlocked");
      }
      setHasGenerated(true);
      setBackdrop(bd);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.jobError || e?.message;
      let msg;
      if (detail === "photo_studio_no_credits") {
        msg = "You're out of credits — top up or upgrade to keep generating studio photos.";
      } else if (detail === "photo_studio_locked_upgrade_reseller") {
        msg = "Photo Studio is a Reseller/Pro feature.";
      } else if (typeof detail === "string" && detail.includes("no stored images")) {
        msg = "NO_IMAGES";
      } else if (typeof detail === "string" && detail.startsWith("photo_studio_failed")) {
        msg = "Enhancement failed — try a clearer photo.";
      } else if (detail === "generation_timeout") {
        msg = "Generation took too long. Please try again.";
      } else if (typeof detail === "string" && detail.startsWith("enrich_failed")) {
        msg = "Description generation failed. Please retry.";
      } else {
        msg = detail || "Photo Studio unavailable. Please try again.";
      }
      setLastError(msg);
      if (msg !== "NO_IMAGES") toast.error(msg);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="glass p-4 sm:p-6"
      data-testid="photo-studio-card"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-[#047857]">
            <Sparkles size={12} /> Photo Studio
          </div>
          <h3 className="font-display text-lg sm:text-xl font-semibold text-white mt-0.5">
            Turn your snap into a studio shot.
          </h3>
        </div>
        {!unlocked && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F1F5F9] border border-[#E9ECEF] text-[10px] font-semibold text-[#64748B]">
            <Lock size={10} /> Reseller & Pro
          </span>
        )}
      </div>

      {!unlocked ? (
        <div
          className="relative rounded-2xl border border-[#E9ECEF] overflow-hidden"
          data-testid="photo-studio-locked-preview"
          style={{ minHeight: 320 }}
        >
          {previewBusy && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#F1F5F9]">
              <div className="text-center">
                <Loader2 className="animate-spin mx-auto mb-2 text-[#64748B]" size={18} />
                <div className="text-xs text-[#64748B]">Generating your studio preview…</div>
              </div>
            </div>
          )}
          {preview?.studio_b64 && (
            <img
              src={`data:image/jpeg;base64,${preview.studio_b64}`}
              alt="Studio preview (blurred)"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "blur(14px) saturate(1.1)", transform: "scale(1.06)" }}
              draggable={false}
              data-testid="photo-studio-preview-img"
            />
          )}
          {previewErr && !preview && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#F1F5F9]" style={{
              backgroundImage: "linear-gradient(180deg,#FAFBFC 0%,#F1F5F9 100%)"
            }} />
          )}
          {/* Gradient scrim + CTA */}
          <div className="absolute inset-0 flex items-end p-4 sm:p-5"
               style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.72) 62%, rgba(255,255,255,0.92) 100%)" }}>
            <div className="w-full">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0F172A] text-white text-[10px] font-semibold tracking-wider" data-force-white="true">
                  <Sparkles size={9} /> YOUR ITEM
                </span>
                <span className="text-[10px] uppercase tracking-widest text-[#64748B] font-semibold">preview</span>
              </div>
              <div className="text-sm sm:text-base font-semibold text-[#0F172A] leading-snug">
                Your item, on a studio backdrop — unlock the full-res version
              </div>
              <div className="text-[12px] text-[#475569] mt-1 mb-3 leading-relaxed">
                Included with every Reseller and Pro scan. Removes clutter, unrolls creases, generates a listing-ready flat-lay.
              </div>
              <button
                data-testid="photo-studio-unlock-btn"
                onClick={() => onUpgrade?.("reseller")}
                className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2.5 rounded-full bg-[#0F172A] text-white text-xs font-semibold hover:bg-[#1E293B] transition-colors min-h-[44px]"
              >
                <span className="whitespace-normal sm:whitespace-nowrap">Unlock Photo Studio — from $14.99/mo</span>
                <ArrowUpRight size={12} className="flex-shrink-0" />
              </button>
            </div>
          </div>
        </div>
      ) : !result ? (
        <div className="text-center py-4" data-testid="photo-studio-precheck">
          {!hasGenerated && !busy && (
            <p className="text-[11.5px] text-[#64748B] mb-2.5 max-w-md mx-auto">
              <span className="font-semibold text-[#475569]">Tip:</span> lay the item flat with the whole product inside the frame — nothing cut off at the edges.
            </p>
          )}
          {paysCredit && !hasGenerated && !busy && (
            <div className="flex w-fit mx-auto items-center gap-2 px-3.5 py-2 mb-3 rounded-full bg-[#FFF7ED] border border-[#FDBA74] text-[12.5px] text-[#9A3412] font-medium">
              <span aria-hidden="true">{"⚡"}</span>
              <span>1 credit unlocks all 4 backdrops for this item · <span className="font-semibold">Free on Reseller &amp; Pro</span></span>
            </div>
          )}
          <button
            data-testid="photo-studio-run-btn"
            onClick={() => runEnhance()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ minHeight: "44px" }}
          >
            {busy ? (
              <><Loader2 size={14} className="animate-spin" /> Generating your listing…</>
            ) : lastError ? (
              <><Sparkles size={14} /> {lastError === "NO_IMAGES" ? "Run a new scan above" : "Try again"}</>
            ) : (
              <><Sparkles size={14} /> {paysCredit && !hasGenerated ? "Generate — 1 credit" : "Generate studio shot"}</>
            )}
          </button>

          {busy && progress && (
            <div className="mt-3 max-w-xs mx-auto" data-testid="photo-studio-progress">
              <div className="h-1.5 rounded-full bg-[#E9ECEF] overflow-hidden">
                <div
                  className="h-full bg-[#0F172A] transition-all duration-500"
                  style={{ width: `${Math.max(5, progress.progress || 0)}%` }}
                />
              </div>
              <div className="text-[11px] text-[#64748B] mt-1.5 font-mono-num">
                {progress.stage === "segmenting" && "Cutting out your item…"}
                {progress.stage === "compositing" && "Placing it on the backdrop…"}
                {(progress.stage === "queued" || progress.stage === "waiting") && "Queued…"}
                {progress.stage === "running" && "Working on it…"}
                {progress.stage === "cached" && "Loading cached result…"}
              </div>
            </div>
          )}

          {lastError === "NO_IMAGES" && !busy && (
            <div
              className="mt-3 max-w-sm mx-auto p-3 rounded-ds-sm bg-surface-sunken border border-border-default"
              data-testid="photo-studio-no-images"
            >
              <p className="text-[12px] text-ink-700 leading-relaxed">
                Studio photos aren&apos;t available for this scan — it has no stored photos. Run a new scan to use Photo Studio. No credit was used.
              </p>
            </div>
          )}
          {lastError && lastError !== "NO_IMAGES" && !busy && (
            <div
              className="mt-3 max-w-sm mx-auto p-3 rounded-xl bg-[#FEE2E2] border border-[#DC2626]/30"
              data-testid="photo-studio-error"
            >
              <p className="text-[12px] text-[#991B1B] leading-relaxed">{lastError}</p>
              <p className="text-[11px] text-[#7F1D1D]/80 mt-1">
                Click <strong>Try again</strong> — your credit hasn&apos;t been charged.
              </p>
            </div>
          )}

          {!busy && !lastError && (
            <div className="text-[11px] text-[#64748B] mt-2 max-w-sm mx-auto leading-relaxed">
              v2 keeps your item&apos;s <strong>actual pixels</strong> — tag, colour, creases stay exactly as photographed. Only the backdrop changes.
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Backdrop chips */}
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {BACKDROPS.map((b) => (
              <button
                key={b.id}
                data-testid={`photo-studio-bg-${b.id}`}
                onClick={() => runEnhance(b.id)}
                disabled={busy}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                  backdrop === b.id ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#475569] border-[#E9ECEF] hover:border-[#0F172A]"
                }`}
                {...(backdrop === b.id ? { "data-force-white": "true" } : {})}
              >
                <span className="w-3 h-3 rounded-full border border-[#0F172A]/20" style={{ background: b.swatch }} />
                {b.label}
              </button>
            ))}
            {busy && <Loader2 size={12} className="animate-spin text-[#64748B]" />}
          </div>

          {/* Before / after — side-by-side (no slider). Simpler on mobile,
              and makes it explicit that both photos belong to the user. */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3" data-testid="photo-studio-compare">
            <figure className="flex flex-col">
              <div className="relative rounded-2xl overflow-hidden border border-[#E9ECEF] bg-[#F1F5F9] aspect-square">
                <img
                  src={`data:image/jpeg;base64,${result.original_b64}`}
                  alt="Your original photo"
                  className="absolute inset-0 w-full h-full object-contain"
                  data-testid="photo-studio-before"
                  draggable={false}
                />
                <span
                  className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#0F172A]/90 text-white text-[10px] font-semibold"
                  data-force-white="true"
                >
                  REAL PHOTO
                </span>
              </div>
              <button
                data-testid="photo-studio-download-original"
                onClick={() => downloadDataUrl(result.original_b64, `real-photo-${scanId.slice(0,8)}.jpg`)}
                className="mt-2 flex items-center justify-center gap-1.5 px-3 py-2.5 min-h-[44px] rounded-full bg-white border border-[#E9ECEF] text-[#0F172A] text-xs font-semibold hover:border-[#0F172A] transition-colors"
              >
                <Download size={12} /> Download
              </button>
            </figure>

            <figure className="flex flex-col">
              <div className="relative rounded-2xl overflow-hidden border border-[#E9ECEF] bg-[#F1F5F9] aspect-square">
                <img
                  src={`data:image/jpeg;base64,${result.studio_b64}`}
                  alt="AI-enhanced studio shot"
                  className="absolute inset-0 w-full h-full object-contain"
                  data-testid="photo-studio-after"
                  draggable={false}
                />
                <span
                  className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#B45309] text-white text-[10px] font-semibold flex items-center gap-1"
                  data-force-white="true"
                >
                  <Sparkles size={9} /> STUDIO
                </span>
              </div>
              <button
                data-testid="photo-studio-download-studio"
                onClick={() => downloadDataUrl(result.studio_b64, `studio-${scanId.slice(0,8)}.jpg`)}
                className="mt-2 flex items-center justify-center gap-1.5 px-3 py-2.5 min-h-[44px] rounded-full bg-[#0F172A] text-white text-xs font-semibold hover:bg-[#1E293B] transition-colors"
              >
                <Download size={12} /> Download
              </button>
            </figure>
          </div>

            {/* Rough-segmentation warning — shown only when reliability heuristic
                flags an unreliable cutout. Both images stay downloadable; the
                user judges better than any auto-reject heuristic could. */}
            {result.reliability === "rough" && (
              <div
                className="mt-3 p-3 rounded-xl bg-[#FEF3C7] border border-[#F59E0B]/40 flex items-start gap-2"
                data-testid="photo-studio-rough-warning"
              >
                <span className="text-[#B45309] text-sm">⚠</span>
                <p className="text-[11px] text-[#78350F] leading-relaxed">
                  <strong>The cutout looks rough on this photo.</strong>{" "}
                  Loose threads or complex shapes can trip segmentation — use the
                  original instead if the studio shot doesn&apos;t look right.
                </p>
              </div>
            )}

            {/* Compliance note — v2 preserves your real pixels, so this is
                genuinely YOUR photo on a new backdrop. Still worth flagging
                that a synthetic backdrop is not the item's real environment. */}
            <div className="mt-3 p-3 rounded-xl bg-[#EFF6FF] border border-[#3B82F6]/30 flex items-start gap-2">
              <span className="text-[#1E40AF] text-sm">ℹ</span>
              <p className="text-[11px] text-[#1E3A8A] leading-relaxed">
                <strong>Your real photo, new backdrop.</strong> Item pixels are
                preserved bit-for-bit — same tag, same colour, same creases.
                Only the surface behind it changes. Fair to use as a hero shot;
                include your real photos too so buyers see the full context.
              </p>
            </div>

          <p className="text-[11px] text-[#64748B] mt-2 text-center">Both photos are yours to keep.</p>
        </div>
      )}
    </motion.div>
  );
}
